/**
 * @fileoverview Canonical config and resolved-output types for the document rendering pipeline.
 * These types were previously duplicated identically in the worker (`documentDataTransformer.ts`)
 * and the frontend (`documentFieldBindings.ts`). This module is the single source of truth
 * (G-DRY-TYPES).
 * @module document-render/documentConfig
 *
 * Responsibilities: - Define the shape of a legacy `documentConfig` (the 15-section builder model)
 *                     and every `Resolved*` sub-structure produced by the data transformer / preview
 *                     resolver. Both sides import from here; neither may define its own copies.
 * Dependencies:     - ./renderPrimitives (BooleanDisplayStyle — used in AdvancedFieldFormatting).
 * Architecture:     - Pure types + type unions; no runtime code, no I/O.
 * Notes:            - Fields that exist only in the worker (e.g. `formResponses` on
 *                     `DocumentRenderData`, `color` on `ResolvedMetadataField`) are kept as
 *                     optional so both consumers compile without import errors. The worker
 *                     populates them; the frontend ignores them.
 *                   - `PreviewDataSource` and `SAMPLE_PREVIEW_DATA` remain in the frontend
 *                     package because they are presentation-only concerns.
 */

import type { BooleanDisplayStyle } from './renderPrimitives';
import type { DocumentLayout, ElementStyle } from './documentLayout';

// ---------------------------------------------------------------------------
// Document Config types (the builder / template model)
// ---------------------------------------------------------------------------

/**
 * @contract DocumentConfig
 * Purpose: Complete template configuration for a document (15-section builder model).
 * Fields:  documentType, layoutMode, page dimensions, section definitions, per-section configs.
 * Rules:   `sections` determines render order; each section carries an `enabled` flag.
 * Sync:    Part of the report template document (local-first via Dexie).
 */
export interface DocumentConfig {
  documentType: string;
  layoutMode: 'form' | 'letter' | 'standard';
  pageSize: 'letter' | 'a4' | 'legal';
  orientation: 'portrait' | 'landscape';
  approvalMode?: string;
  reportDataScope?: 'singleCase' | 'complexVisit';
  sections: SectionDef[];
  complexVisitSummary?: ComplexVisitSummaryConfig;
  complexParticipants?: ComplexParticipantsConfig;
  header?: HeaderConfig;
  recipientBlock?: RecipientBlockConfig;
  subjectLine?: SubjectLineConfig;
  salutation?: SalutationConfig;
  bodyText?: BodyTextConfig;
  metadata?: MetadataConfig;
  violationConfig?: ViolationConfig;
  photoConfig?: PhotoConfig;
  comments?: CommentsConfig;
  contactBlock?: ContactBlockConfig;
  signatures?: SignaturesConfig;
  legalText?: LegalTextConfig;
  footerConfig?: FooterConfig;
  staticContent?: StaticContentConfig;
  coverPage?: CoverPageConfig;
  /**
   * Named color-theme preset applied to the rendered document.
   * Used as fallback when no runtime department branding is available.
   * Absent or 'none' means use the renderer's built-in CSS fallback colors.
   */
  themePreset?: ThemePresetId;
  /**
   * Custom theme overrides (Phase E2). Applied on top of — and beating — the named
   * `themePreset`, so a department can fine-tune primary/accent/heading colors and the
   * document base font without being limited to the preset gallery. Per-element styles
   * (E1) still beat the theme. Absent fields fall back to the preset / renderer defaults.
   */
  themeCustom?: DocumentThemeCustom;
  page?: PageSettings;
  /** Legacy flag: when true, renders a "DRAFT" watermark unless page.watermark overrides it. */
  draftWatermark?: boolean;
}

/**
 * @contract PageSettings
 * Purpose: Page-level layout for the rendered document (G-LAYOUT).
 * Fields:  marginsInches (per-side), watermark (text/opacity/angle), border (width/style/color).
 * Rules:   Margins default to 0.5in; watermark/border omitted = none.
 * Sync:    Part of documentConfig (local-first template).
 */
export interface PageSettings {
  marginsInches?: { top?: number; right?: number; bottom?: number; left?: number };
  watermark?: { text: string; opacity?: number; angle?: number };
  border?: { width: number; style: 'solid' | 'double' | 'dashed' | 'dotted'; color: string };
}

/**
 * @contract ComplexVisitSummaryConfig
 * Purpose: Builder flags for the complex visit summary section.
 * Fields: showComplexName, showAnchor, showVisitDate, showParticipantCounts, showStandaloneSummary.
 * Rules: Absent runtime flags use conservative true defaults in the renderer.
 * Sync: Persisted through DocumentConfig/local template transforms.
 */
export interface ComplexVisitSummaryConfig {
  showComplexName: boolean;
  showAnchor: boolean;
  showVisitDate: boolean;
  showParticipantCounts: boolean;
  showStandaloneSummary: boolean;
}

/**
 * @contract ComplexParticipantsConfig
 * Purpose: Builder flags for governed participant rendering.
 * Fields: Inclusion, page-break, entity metadata, nested detail, and sort flags.
 * Rules: Absent runtime flags use conservative true defaults; unresolved permits/assets are omitted.
 * Sync: Persisted through DocumentConfig/local template transforms.
 */
export interface ComplexParticipantsConfig {
  includeRootEntity: boolean;
  includeGovernedMembers: boolean;
  includeStandaloneSummary: boolean;
  startEachEntityOnNewPage: boolean;
  showEntityHeader: boolean;
  showEntityAddress: boolean;
  showEntityStatus: boolean;
  showQuestionsResponses: boolean;
  showViolations: boolean;
  showCorrections: boolean;
  showPermits: boolean;
  showAssets: boolean;
  showPhotos: boolean;
  showSignatures: boolean;
  entitySort: 'visitOrder' | 'entityName' | 'entityCategory';
}

export interface SectionDef {
  id: string;
  label: string;
  description?: string;
  enabled: boolean;
}

export interface HeaderConfig {
  showLogo?: boolean;
  logoPosition?: 'left' | 'center' | 'right';
  showSecondLogo?: boolean;
  secondLogoPosition?: 'left' | 'center' | 'right';
  showDepartmentName?: boolean;
  showDepartmentAddress?: boolean;
  showDepartmentPhone?: boolean;
  showDepartmentFax?: boolean;
  showDepartmentWebsite?: boolean;
  customTitle?: string;
  customSubtitle?: string;
  /** Per-sub-element typography for the custom title (Phase E styling). */
  titleStyle?: ElementStyle;
  /** Per-sub-element typography for the custom subtitle (Phase E styling). */
  subtitleStyle?: ElementStyle;
}

export interface RecipientBlockConfig {
  style: 'grid' | 'letter-address';
  showOwnerName?: boolean;
  showContactNumber?: boolean;
  showApn?: boolean;
  showInspectionAddress?: boolean;
  showEmailAddress?: boolean;
  showMailingAddress?: boolean;
  showInspectionNumber?: boolean;
}

export interface SubjectLineConfig {
  prefix?: string;
  template?: string;
}

export interface SalutationConfig {
  text?: string;
}

export interface BodyTextConfig {
  paragraphs?: BodyParagraph[];
}

export interface BodyParagraph {
  id: string;
  content: string;
  condition?: 'always' | 'hasViolations' | 'noViolations';
  isHtml?: boolean;
}

export interface MetadataConfig {
  showComplianceStatus?: boolean;
  showInspectionType?: boolean;
  showInspectionDate?: boolean;
  showSlope?: boolean;
  showInspector?: boolean;
  showAcreageEligible?: boolean;
  showPhotosFlag?: boolean;
  showCurbsideVacant?: boolean;
  showInsuranceSolicited?: boolean;
  showComplaintConstruction?: boolean;
}

/**
 * @contract CheckboxMarkStyle
 * Purpose: How a violation/checklist item's mark glyph renders.
 * Fields:  'box' (filled/empty square — legacy default) | 'check' (✓) |
 *          'cross' (✗) | 'checkCross' (green ✓ when passed, red ✗ when failed).
 * Rules:   Template author picks the style; preview and PDF must agree.
 * Sync:    N/A (pure rendering choice; persisted on ViolationConfig).
 */
export type CheckboxMarkStyle = 'box' | 'check' | 'cross' | 'checkCross';

export interface ViolationConfig {
  displayFormat: 'checklist' | 'narrative' | 'table';
  /**
   * How violations are grouped. 'question' nests each violation (code + occurrences) under the
   * inspection question that produced it, with violations that have no parent question collected
   * into a trailing "Additional Violations" bucket (G-VIOL-QGROUP).
   */
  groupBy?: 'zone' | 'category' | 'severity' | 'none' | 'question';
  showCodeReferences?: boolean;
  showLocationDescriptions?: boolean;
  conditionalDisplay?: 'always' | 'whenPresent';
  /** Color each item by its pass/fail result (pass green, fail red). G-VIOL-COLOR. */
  resultColoring?: boolean;
  /** Hex color applied to passing items when resultColoring is on. Default #16a34a. */
  passColor?: string;
  /** Hex color applied to failing items when resultColoring is on. Default #dc2626. */
  failColor?: string;
  /** Glyph used for the checklist mark. Default 'box' (legacy filled/empty square). */
  checkboxMarkStyle?: CheckboxMarkStyle;
}

export interface PhotoConfig {
  layout?: 'inline' | 'grid' | 'appendix';
  maxPhotos?: number;
  showCaptions?: boolean;
  thumbnailSize?: 'small' | 'medium' | 'large';
}

export interface CommentsConfig {
  showCommentsBox?: boolean;
  additionalFields?: Array<{ id: string; label: string; type: string }>;
}

export interface ContactBlockConfig {
  introText?: string;
  showPhone?: boolean;
  showEmail?: boolean;
  showFax?: boolean;
}

export interface SignaturesConfig {
  style: 'full' | 'initials' | 'digital';
  inspectorSignature?: boolean;
  responsiblePartySignature?: boolean;
  witnessSignature?: boolean;
  dateFields?: boolean;
  printedNameFields?: boolean;
  showTitle?: boolean;
}

export interface LegalTextConfig {
  enabled?: boolean;
  content?: string;
  position?: 'inline' | 'footer';
}

export interface FooterConfig {
  showDepartmentInfo?: boolean;
  showPageNumbers?: boolean;
  customText?: string;
}

export interface StaticContentConfig {
  type?: 'uploadedFile' | 'html';
  label?: string;
  uploadedFileId?: string;
  htmlContent?: string;
}

/**
 * @contract CoverPageConfig
 * Purpose: Controls which elements appear on the optional cover-page section.
 * Fields:  showLogo, showEntityInfo, showInspectionSummary, showResultBadge
 * Rules:   All fields are optional; defaults to showing everything when omitted.
 * Sync:    Part of DocumentConfig; persisted with the report template.
 */
export interface CoverPageConfig {
  /** Display the department logo at the top of the cover. */
  showLogo?: boolean;
  /** Display the entity name, address, and APN. */
  showEntityInfo?: boolean;
  /** Display inspection date, inspector name, and inspection type. */
  showInspectionSummary?: boolean;
  /** Display a large compliance-status badge (Pass / Violations Found). */
  showResultBadge?: boolean;
}

// ---------------------------------------------------------------------------
// Theme presets (G-THEME) — named color palettes for the document renderer
// ---------------------------------------------------------------------------

/**
 * @contract ThemePreset
 * Purpose: Canonical color palette applied to the rendered document when no
 *          runtime department branding is available (preview, export, PDF).
 * Fields:  id (slug), label (display name), primary + secondary hex colors
 * Rules:   'none' means use the renderer's built-in CSS fallback colors.
 * Sync:    Stored as themePreset: ThemePresetId on DocumentConfig.
 */
export type ThemePresetId =
  | 'none'
  | 'classic'
  | 'fire'
  | 'ember'
  | 'slate'
  | 'forest'
  | 'midnight';

export interface ThemePreset {
  id: ThemePresetId;
  label: string;
  /** Primary brand color (hex, e.g. '#1e40af'). Empty string for 'none'. */
  primary: string;
  /** Secondary brand color. Defaults to primary when absent. */
  secondary: string;
}

/**
 * @contract DocumentThemeCustom
 * Purpose: User-authored theme overrides layered on top of a named preset (Phase E2),
 *          so departments can pick exact colors + a base font instead of being limited
 *          to the preset gallery.
 * Fields:  primary (brand/title color), accent (secondary color), heading (heading text
 *          color, defaults to primary), baseFont (document body font-family stack).
 * Rules:   Every field optional; absent = fall back to preset, then renderer default.
 *          Empty string is treated as "unset". Per-element styles (E1) still win.
 * Sync:    Stored as themeCustom on DocumentConfig (local-first template).
 */
export interface DocumentThemeCustom {
  primary?: string;
  accent?: string;
  heading?: string;
  baseFont?: string;
}

/** Canonical preset gallery ordered for display in the theme-selector UI. */
export const THEME_PRESETS: ThemePreset[] = [
  { id: 'none',     label: 'Department Default', primary: '',        secondary: '' },
  { id: 'classic',  label: 'Classic Blue',        primary: '#1e40af', secondary: '#1d4ed8' },
  { id: 'fire',     label: 'Fire Red',            primary: '#dc2626', secondary: '#b91c1c' },
  { id: 'ember',    label: 'Ember Orange',         primary: '#ea580c', secondary: '#c2410c' },
  { id: 'slate',    label: 'Slate Gray',           primary: '#475569', secondary: '#334155' },
  { id: 'forest',   label: 'Forest Green',         primary: '#16a34a', secondary: '#15803d' },
  { id: 'midnight', label: 'Midnight Indigo',      primary: '#4f46e5', secondary: '#4338ca' },
];

// ---------------------------------------------------------------------------
// Resolved output types — consumed by templates / renderer / preview
// ---------------------------------------------------------------------------

export interface ResolvedHeader {
  logoUrl?: string;
  secondLogoUrl?: string;
  logoPosition: string;
  secondLogoPosition: string;
  departmentName?: string;
  departmentAddress?: string;
  departmentPhone?: string;
  departmentFax?: string;
  departmentWebsite?: string;
  title?: string;
  subtitle?: string;
  /** Resolved per-sub-element typography passed through from HeaderConfig (Phase E styling). */
  titleStyle?: ElementStyle;
  subtitleStyle?: ElementStyle;
}

export interface ResolvedRecipientBlock {
  style: string;
  fields: Array<{ label: string; value: string }>;
}

export interface ResolvedSubjectLine {
  text: string;
}

export interface ResolvedSalutation {
  text: string;
}

export interface ResolvedBodyParagraph {
  id: string;
  content: string;
  isHtml?: boolean;
}

export interface ResolvedMetadataField {
  label: string;
  value: string;
  key: string;
  /** Optional hex color applied to the value (e.g. compliance status) — G-FID-02. */
  color?: string;
}

/**
 * @contract ResolvedViolationOccurrence
 * Purpose: One recorded occurrence of a violation (the inspection may flag the same code in
 *          several places). Listed beneath the violation code in the question-grouped layout.
 * Fields:  zone, location, notes, status — all optional; missing values collapse to ''.
 * Rules:   Source-agnostic; each adapter (Mongo canonical / Dexie loose) maps its own shape.
 * Sync:    N/A (pure value object built at render time).
 */
export interface ResolvedViolationOccurrence {
  /** Source occurrence identity, used to join per-occurrence photos when the photo layout is
   * 'inline' (DFE-005). Optional so loose/sample sources that carry no id still render. */
  id?: string;
  zone?: string;
  location?: string;
  notes?: string;
  status?: string;
  /** Photos tied to this occurrence, attached by the assembler for the inline photo layout
   * (DFE-005). Rendered beneath the violation; absent for gallery/appendix layouts. */
  photos?: ResolvedPhoto[];
  /** Correction lifecycle status: pending | inProgress | repaired | retracted | exception | extensionGranted.
   * Used by the question-grouped renderer to display per-occurrence repair state. */
  correctionStatus?: string;
  /** ISO date when the violation was first discovered/observed. */
  discoveredAt?: string;
  /** ISO date when the correction was completed (repaired). Mapped from `correctionCompletedAt`
   * on the canonical occurrence by each adapter. */
  fixedAt?: string;
  /** Free-text notes describing the correction/repair performed. */
  fixedNotes?: string;
}

export interface ResolvedViolationItem {
  code: string;
  description: string;
  checked: boolean;
  /** Complete source citations when supplied by the inspection/code-set record. */
  fireCode?: string;
  ieCode?: string;
  text?: string;
  codeReference?: string;
  location?: string;
  zone?: string;
  severity?: string;
  status?: string;
  /**
   * Explicit pass/fail result for conditional coloring (G-VIOL-COLOR). When omitted the
   * renderer derives it from `status` keywords, then falls back to `!checked`.
   */
  passed?: boolean;
  /**
   * Question linkage for groupBy:'question' (G-VIOL-QGROUP). `questionId` is the stable grouping
   * key (the inspection response fieldId); `questionText` is the heading shown above the violation.
   * `source` distinguishes question-derived violations from ad-hoc "additional" ones (the latter
   * land in the trailing Additional Violations bucket). All optional so the other groupBy modes
   * and legacy data are unaffected.
   */
  questionId?: string;
  questionText?: string;
  source?: 'question' | 'additional';
  /** Every recorded occurrence of this violation, listed under the code in the question layout. */
  occurrences?: ResolvedViolationOccurrence[];
  /**
   * Photos tied to this violation's occurrences, attached by the assembler when the photo layout
   * is 'inline' (DFE-005). The renderer places these beneath the violation instead of in the
   * standalone gallery. Absent for gallery/appendix layouts and table format.
   */
  photos?: ResolvedPhoto[];
  /**
   * Internal violation document _id carried through from extraction (DFE-005 fallback).
   * Used by distributeInlinePhotos to match photos by violationId when occurrenceId is missing
   * or doesn't match any occurrence (legacy data, sync gaps). Not rendered in the HTML output.
   */
  _violationId?: string;
}

export interface ResolvedViolationGroup {
  groupLabel: string;
  groupKey: string;
  items: ResolvedViolationItem[];
  /** True for the trailing "Additional Violations" bucket in groupBy:'question' (G-VIOL-QGROUP). */
  isAdditional?: boolean;
}

export interface ResolvedPhoto {
  blobUrl: string;
  caption?: string;
  takenAt?: string;
  /** Worker populates these for violation-tagged photos; frontend may omit. */
  isViolationPhoto?: boolean;
  violationCode?: string;
  violationDescription?: string;
  /**
   * Occurrence/violation linkage carried from extraction so the assembler can place a photo
   * beneath its violation for the inline layout (DFE-005). `occurrenceId` is the precise join
   * key (occurrence-scoped photos); `violationId` is the parent violation. Optional so general
   * (non-violation) photos and loose sources are unaffected.
   */
  occurrenceId?: string;
  violationId?: string;
}

export interface ResolvedComments {
  externalNotes?: string;
  internalNotes?: string;
  additionalFields: Array<{ id: string; label: string; type: string; value?: string }>;
}

export interface ResolvedContactBlock {
  introText?: string;
  phone?: string;
  email?: string;
  fax?: string;
}

export interface ResolvedSignature {
  role: string;
  label: string;
  /** Custom role label (e.g. "Battalion Chief"); worker populates, frontend may omit. */
  roleLabel?: string;
  name?: string;
  signatureUrl?: string;
  signedAt?: string;
  showDate: boolean;
  showPrintedName: boolean;
  showTitle: boolean;
  title?: string;
}

export interface ResolvedSignatures {
  style: string;
  signatures: ResolvedSignature[];
}

export interface ResolvedLegalText {
  content: string;
  position: string;
}

export interface ResolvedFooter {
  departmentName?: string;
  departmentPhone?: string;
  showPageNumbers: boolean;
  customText?: string;
}

export interface ResolvedStaticContent {
  type: string;
  label?: string;
  uploadedFileId?: string;
  htmlContent?: string;
}

// ---------------------------------------------------------------------------
// Form Response types (worker produces these; frontend preview does not)
// ---------------------------------------------------------------------------

export interface ResolvedFormResponseField {
  fieldId: string;
  label: string;
  type: string;
  value: string;
  rawValue: any;
  isImage: boolean;
  imageDataUri?: string;
}

export interface ResolvedFormSection {
  sectionId: string;
  sectionTitle: string;
  fields: ResolvedFormResponseField[];
}

export interface ResolvedFormResponseGroup {
  formId: string;
  formName: string;
  sectionGroups: ResolvedFormSection[];
}

// ---------------------------------------------------------------------------
// Advanced Linked Document Types
// ---------------------------------------------------------------------------

export type AdvancedDocumentMode = 'standard' | 'advancedLinked';

/**
 * @contract AdvancedBinding
 * Purpose: Advanced-document mode marker + linked template settings.
 * Fields:  mode, linked template IDs, fieldCatalogVersion, render controls.
 * Rules:   `renderAdvancedSection` defaults to true when absent.
 * Sync:    Part of the report template document.
 */
export interface AdvancedBinding {
  mode: AdvancedDocumentMode;
  linkedInspectionTemplateId?: string;
  linkedInspectionTemplateVersion?: number;
  linkedInspectionTemplateName?: string;
  lockTemplateLink?: boolean;
  fieldCatalogVersion?: number;
  /**
   * When false, suppresses the auto-appended "Inspection Data" section at the
   * bottom of the PDF. Defaults to true (backwards compatible).
   */
  renderAdvancedSection?: boolean;
  /**
   * Customisable heading for the advanced-fields table. Defaults to
   * "Inspection Data" when absent.
   */
  advancedSectionLabel?: string;
}

export type AdvancedFieldRenderMode =
  | 'inlineText'
  | 'block'
  | 'tableCell'
  | 'checkbox'
  | 'badge'
  | 'photo'
  | 'richText';

export type AdvancedFieldSourceType =
  | 'templateResponse'
  | 'templateField'
  | 'systemMergeField';

export interface AdvancedFieldFormatting {
  dateFormat?: string;
  booleanStyle?: BooleanDisplayStyle;
  trueLabel?: string;
  falseLabel?: string;
  joinWith?: string;
  numberFormat?: string;
}

/**
 * @contract AdvancedFieldPlacement
 * Purpose: One advanced-field placement on an advancedLinked template.
 * Fields:  id, label, sourceType, bindingKey, fieldId, render config, formatting.
 * Rules:   `criticalField` values not sourced from a response are withheld/marked (B6).
 * Sync:    Part of the report template document.
 */
export interface AdvancedFieldPlacement {
  id: string;
  label: string;
  sourceType: AdvancedFieldSourceType;
  bindingKey: string;
  fieldId?: string;
  formId?: string;
  formName?: string;
  fieldLabel?: string;
  renderMode: AdvancedFieldRenderMode;
  fallbackText?: string;
  includeWhenEmpty?: boolean;
  formatting?: AdvancedFieldFormatting;
  criticalField?: boolean;
  nonResponseBehavior?: 'withhold' | 'mark';
}

export type AdvancedFieldResolvedFrom =
  | 'response'
  | 'snapshot-default'
  | 'system-field'
  | 'fallback';

export interface ResolvedAdvancedField {
  id: string;
  label: string;
  value: string;
  renderMode: AdvancedFieldRenderMode;
  resolvedFrom: AdvancedFieldResolvedFrom;
}

/**
 * @contract ComplexVisitSummaryRenderData
 * Purpose: Resolved visit-level values consumed by the complex summary renderer.
 * Fields: Complex/anchor names, visit date, and governed/standalone counts.
 * Rules: Values are already resolved; renderer escapes dynamic strings.
 * Sync: Ephemeral render input only.
 */
export interface ComplexVisitSummaryRenderData {
  complexName: string;
  anchorName: string;
  visitDate: string;
  governedCount: number;
  standaloneCount: number;
}

/**
 * @contract ComplexStandaloneSummaryRow
 * Purpose: One standalone participant row in a complex visit summary.
 * Fields: Entity name/address and resolved status.
 * Rules: This row never carries full standalone report content.
 * Sync: Ephemeral render input only.
 */
export interface ComplexStandaloneSummaryRow {
  entityName: string;
  entityAddress?: string;
  status: string;
}

/**
 * @contract ComplexParticipantRenderData
 * Purpose: One governed participant and its nested single-case render data.
 * Fields: Stable case/event identity, explicit root marker, entity metadata,
 *   participation mode, optional permits/assets, and nested data.
 * Rules: `isRoot` is authoritative; eventIndex is display/order metadata only.
 * Sync: Ephemeral render input only.
 */
export interface ComplexParticipantRenderData {
  caseId: string;
  eventIndex: number;
  isRoot: boolean;
  entityName: string;
  entityAddress?: string;
  entityCategory: string;
  entityStatus?: string;
  participationMode: 'complex-governed';
  permits?: Array<{ label: string; status?: string }>;
  assets?: Array<{ label: string; status?: string }>;
  data: DocumentRenderData;
}

// ---------------------------------------------------------------------------
// DocumentRenderData — the fully-resolved shape consumed by templates
// ---------------------------------------------------------------------------

/**
 * @contract DocumentRenderData
 * Purpose: Fully-resolved, template-ready data for one document render pass.
 * Fields:  All resolved sub-structures + section config + metadata.
 * Rules:   This is the boundary contract between the data transformer and the
 *          renderer. Both the worker (shared renderer A4+) and the frontend
 *          preview consume this shape.
 * Sync:    Ephemeral — built per render, never persisted.
 */
export interface DocumentRenderData {
  documentConfig: DocumentConfig;
  header?: ResolvedHeader;
  recipientBlock?: ResolvedRecipientBlock;
  subjectLine?: ResolvedSubjectLine;
  salutation?: ResolvedSalutation;
  bodyParagraphs?: ResolvedBodyParagraph[];
  metadataFields?: ResolvedMetadataField[];
  violations?: ResolvedViolationItem[];
  violationGroups?: ResolvedViolationGroup[];
  hasViolations: boolean;
  photos?: ResolvedPhoto[];
  comments?: ResolvedComments;
  contactBlock?: ResolvedContactBlock;
  signatures?: ResolvedSignatures;
  legalText?: ResolvedLegalText;
  footer?: ResolvedFooter;
  staticContent?: ResolvedStaticContent;
  /** Worker populates from inspection template form responses; not used in FE preview. */
  formResponses?: ResolvedFormResponseGroup[];
  advancedFields?: ResolvedAdvancedField[];
  /** Controls visibility of the bottom advanced-fields table in the PDF. */
  renderAdvancedSection?: boolean;
  /** Heading text for the advanced-fields table section. */
  advancedSectionLabel?: string;
  complexVisitSummary?: ComplexVisitSummaryRenderData;
  complexParticipants?: ComplexParticipantRenderData[];
  complexStandaloneSummary?: ComplexStandaloneSummaryRow[];
  /**
   * Optional department brand colors. When present, renderDocumentToHtml injects
   * --brand-primary / --brand-secondary CSS custom properties so section titles,
   * violation codes, and header borders use the department's colors.
   */
  branding?: { primary: string; secondary?: string };
  reportNumber?: string;
  generatedAt?: string;
  currentYear?: number;
  /**
   * Optional band/flow layout (the canvas builder's model) with static-band element HTML
   * already merge-resolved + sanitized. When present, the worker renders the document from
   * this band order via renderLayoutDocumentToHtml instead of the documentConfig.sections order.
   */
  resolvedLayout?: DocumentLayout;
}
